import { Link, useParams } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import CopyButton from '../components/docs/CopyButton'
import Icon from '../components/docs/Icon'
import AgencySitePreview from '../components/missions/AgencySitePreview'
import MissionMarks from '../components/missions/MissionMarks'
import PaletteTable from '../components/missions/PaletteTable'
import NotFoundPage from './NotFoundPage'
import { america, childrenOf, findMission, parentOf, themeFor, type Mission } from '@/content/missions'
import AgencyMark from '../components/marks/AgencyMark'
import { usePrefs } from '@/state/prefs'
import Cased from '@/components/ui/Cased'

const sourceKind = {
  'brand-guide': 'the agency brand guide',
  site: "the agency's website",
  seal: "the agency's seal",
  flag: 'the flag',
} as const

export default function MissionDetailPage() {
  const { slug } = useParams()
  const { prefs, set } = usePrefs()
  const mission = findMission(slug)
  if (!mission) return <NotFoundPage />

  const theme = themeFor(mission)
  const applied = prefs.mission === mission.id
  const snippet = `<link rel="stylesheet" href="/mds/tokens.css">\n<link rel="stylesheet" href="/mds/themes/${mission.id}.css">\n\n<!-- Set light or dark, or omit to follow the visitor's system setting. -->\n<html data-theme="light">`

  return (
    <DocLayout section={{ label: 'Missions', to: '/missions' }}
      eyebrow="Missions"
      title={mission.name}
      wide
      intro={
        <>
          <p>
            <q className="msn-quote">{mission.mission.text}</q>{' '}
            <a className="mds-link" href={mission.mission.url}>
              {mission.kind === 'nation' ? 'Declaration of Independence' : `${mission.short} mission statement`}
            </a>
          </p>
          {mission.fact && (
            <p className="mds-muted">
              {mission.fact.text}{' '}
              <a className="mds-link" href={mission.fact.url}>
                Source
              </a>
            </p>
          )}
        </>
      }
    >
      <div className="msn-actions">
        {applied && mission.id !== america.id ? (
          <button type="button" className="mds-button mds-button--secondary" onClick={() => set('mission', america.id)}>
            Remove theme
          </button>
        ) : (
          <button type="button" className="mds-button mds-button--primary" aria-pressed={applied} onClick={() => set('mission', mission.id)}>
            {applied ? 'Applied to this site' : 'Apply to this site'}
          </button>
        )}
        <a className="mds-button mds-button--ghost" href={`/mds/themes/${mission.id}.css`} download>
          <Icon name="download" size={16} /> {mission.id}.css
        </a>
        <a className="mds-button mds-button--ghost" href={`/mds/themes/${mission.id}.json`} download>
          <Icon name="download" size={16} /> {mission.id}.json
        </a>
      </div>

      <section className="doc-section" aria-labelledby="msn-preview">
        <h2 id="msn-preview"><Cased>Preview</Cased></h2>
        <div className="msn-previews">
          <AgencySitePreview mission={mission} mode="light" />
          <AgencySitePreview mission={mission} mode="dark" />
        </div>
      </section>

      {parentOf(mission) && (
        <section className="doc-section" aria-labelledby="msn-parent">
          <h2 id="msn-parent">{parentOf(mission)!.kind === 'department' ? 'Department' : 'Agency'}</h2>
          <FamilyList members={[parentOf(mission)!]} />
        </section>
      )}
      {childrenOf(mission).length > 0 && (
        <section className="doc-section" aria-labelledby="msn-family">
          <h2 id="msn-family">{mission.kind === 'department' ? 'Agencies in this department' : 'Organizations in this agency'}</h2>
          <FamilyList members={childrenOf(mission)} />
        </section>
      )}
      <section className="doc-section" aria-labelledby="msn-marks">
        <h2 id="msn-marks"><Cased>Marks</Cased></h2>
        <p>
          {mission.kind === 'nation'
            ? 'The flag of the United States in each style. Choosing a style here sets it across the site.'
            : `The flag, seal, logo and motion styles show the ${mission.short} seal or logo; simple and pixel are original emblems drawn from its ${mission.motif}. Choosing a style here sets it across the site.`}
        </p>
        <MissionMarks mission={mission} />
        {mission.seal && (
          <p className="mds-muted msn-credit">
            Seal: <a className="mds-link" href={mission.seal.source}>Wikimedia Commons</a>, a work of the United States
            government.
          </p>
        )}
      </section>

      <section className="doc-section" aria-labelledby="msn-palette">
        <h2 id="msn-palette"><Cased>Palette</Cased></h2>
        <ul className="msn-source">
          {(['primary', 'secondary', 'accent'] as const).map((key) => (
            <li key={key}>
              <span className="msn-source__chip" style={{ background: mission.palette[key] }} aria-hidden="true" />
              <span className="msn-source__key">{key}</span>
              <code>{mission.palette[key]}</code>
            </li>
          ))}
        </ul>
        <p>
          Source palette from{' '}
          <a className="mds-link" href={mission.colorSource.url}>
            {sourceKind[mission.colorSource.kind]}
          </a>
          . Every role below is derived from it; ratios show each role against the color it is read on.
        </p>
        <PaletteTable theme={theme} />
      </section>

      <section className="doc-section" aria-labelledby="msn-adopt">
        <h2 id="msn-adopt"><Cased>Adopt this theme</Cased></h2>
        <p>
          Load the core tokens, then the theme. Set <code>data-theme</code> to <code>light</code> or <code>dark</code>{' '}
          on the root element; without it, the theme follows the visitor's system setting.
        </p>
        <pre className="mds-code" tabIndex={0}>
          <code>{snippet}</code>
        </pre>
        <CopyButton text={snippet} label="Copy snippet" />
      </section>
    </DocLayout>
  )
}

/** Links to related missions, each with its seal. */
function FamilyList({ members }: { members: Mission[] }) {
  return (
    <ul className="msn-family">
      {members.map((related) => (
        <li key={related.id} data-mission={related.id}>
          <Link to={`/missions/${related.id}`} className="msn-family__link">
            <AgencyMark mission={related} variant="seal" className="msn-family__mark" />
            <span>{related.name}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}
